import { ReactElement, useMemo } from 'react';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { DataMode, buildRatioTimeSeries } from './auxSensorChannels';
import { AUX_COLORS, RATIO_ONE_ANNOTATION, auxBaseOptions } from './auxPlotStyle';
import AuxPlotCard from './AuxPlotCard';

interface AuxRatioTimePlotProps {
  gridded: Record<string, (number | null)[]> | undefined;
  circumference: number;
  /** Min linear speed (m/s) — samples below are masked (shared slider). */
  threshold: number;
  dataMode?: DataMode;
  isLoading?: boolean;
}

const CHART_HEIGHT = 320;

/**
 * Slip ratio (wheel speed 1 / wheel speed 2) over time. Samples below the shared
 * min-speed threshold are drawn in grey rather than hidden, so it stays obvious
 * how much of the run the threshold is excluding. A dashed reference line marks
 * ratio = 1 (sensor agreement).
 */
export default function AuxRatioTimePlot({
  gridded,
  circumference,
  threshold,
  dataMode = 'all',
  isLoading = false,
}: AuxRatioTimePlotProps): ReactElement {
  const { included, excluded } = useMemo(
    () => buildRatioTimeSeries(gridded, circumference, threshold, dataMode),
    [gridded, circumference, threshold, dataMode],
  );

  const hasData = useMemo(
    () => included.some((p) => p[1] != null) || excluded.some((p) => p[1] != null),
    [included, excluded],
  );

  const chartSeries = useMemo(
    () => [
      { name: 'Wheel 1 / Wheel 2', data: included },
      { name: `Below ${threshold.toFixed(1)} m/s`, data: excluded },
    ],
    [included, excluded, threshold],
  );

  const options = useMemo<ApexOptions>(() => {
    const base = auxBaseOptions(CHART_HEIGHT);
    return {
      ...base,
      chart: { ...base.chart, type: 'line' },
      colors: [AUX_COLORS.ratio, AUX_COLORS.masked],
      stroke: { curve: 'straight', width: 1.5 },
      markers: { size: 0 },
      xaxis: {
        type: 'numeric',
        title: { text: 'Time (seconds)', style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: string) => `${parseFloat(value).toFixed(0)}s` },
        decimalsInFloat: 0,
      },
      yaxis: {
        title: { text: 'Slip Ratio (Wheel 1 / Wheel 2)', style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: number) => (value != null ? value.toFixed(3) : '') },
      },
      annotations: RATIO_ONE_ANNOTATION,
      tooltip: {
        x: { formatter: (value: number) => `${value.toFixed(1)} s` },
        y: { formatter: (value: number) => (value != null ? value.toFixed(3) : '') },
      },
    };
  }, []);

  return (
    <AuxPlotCard
      title="Slip Ratio vs Time"
      isLoading={isLoading}
      isEmpty={!hasData}
      emptyMessage="No slip-ratio samples for this run (needs two wheel sensors)."
    >
      <ReactApexChart options={options} series={chartSeries} type="line" height={CHART_HEIGHT} />
    </AuxPlotCard>
  );
}
